/**
 * <juice-complete-setup> — the "Complete your setup" attach module (buy-box #7,
 * placed AFTER the primary add-to-cart per CONVERSION-UX §2).
 *
 * Adds the checked accessories to the cart in a single request and integrates with
 * Horizon's cart the native way: it dispatches the theme's own CartLinesUpdateEvent
 * (imported from the same @shopify/events bundle the core theme uses), so the cart
 * drawer auto-opens and the cart line-item components re-render from the returned
 * sections. No fake bundle price is applied — the total shown is the honest sum
 * (DESIGN-BRIEF §7 price honesty); any real bundle discount is a Shopify automatic
 * discount configured separately (DECISIONS "Bundle tier percentages: pending merch").
 */

import { CartLinesUpdateEvent } from '@shopify/events';

const routes = (typeof window !== 'undefined' && window.Theme && window.Theme.routes) || {};
const CART_ADD_URL = routes.cart_add_url || '/cart/add';
const CART_URL = routes.cart_url || '/cart';

class JuiceCompleteSetup extends HTMLElement {
  connectedCallback() {
    this.checkboxes = Array.from(this.querySelectorAll('[data-juice-attach-checkbox]'));
    // The base row = the current product; keep its variant id in sync with the
    // main product form so "Add setup" adds the colour the shopper actually chose.
    this.baseCheckbox = this.querySelector('[data-juice-attach-base]');
    this.mainVariantInput = document.querySelector(
      'product-form-component [name="id"], form[action*="/cart/add"] [name="id"]'
    );
    this.addButton = this.querySelector('[data-juice-attach-add]');
    this.totalEl = this.querySelector('[data-juice-attach-total]');
    this.summaryEl = this.querySelector('[data-juice-attach-summary]');
    this.footer = this.querySelector('[data-juice-attach-footer]');
    this.currency = this.dataset.currency || 'GBP';

    this._onChange = this.update.bind(this);
    this.checkboxes.forEach((cb) => cb.addEventListener('change', this._onChange));
    if (this.addButton) {
      this._onAdd = this.add.bind(this);
      this.addButton.addEventListener('click', this._onAdd);
    }
    this.update();
  }

  disconnectedCallback() {
    this.checkboxes.forEach((cb) => cb.removeEventListener('change', this._onChange));
    if (this.addButton && this._onAdd) this.addButton.removeEventListener('click', this._onAdd);
  }

  selected() {
    return this.checkboxes.filter((cb) => cb.checked);
  }

  money(cents) {
    const value = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: this.currency,
      }).format(value);
    } catch (e) {
      return value.toFixed(2);
    }
  }

  update() {
    const sel = this.selected();
    const count = sel.length;
    const total = sel.reduce((sum, cb) => sum + (parseInt(cb.dataset.price, 10) || 0), 0);

    if (this.footer) this.footer.hidden = count === 0;
    if (this.totalEl) this.totalEl.textContent = this.money(total);
    if (this.summaryEl) {
      const noun = count === 1 ? 'item' : 'items';
      this.summaryEl.textContent = count + ' ' + noun + ' selected';
    }
    if (this.addButton) this.addButton.disabled = count === 0;
  }

  syncBaseVariant() {
    if (this.baseCheckbox && this.mainVariantInput && this.mainVariantInput.value) {
      this.baseCheckbox.dataset.variantId = this.mainVariantInput.value;
    }
  }

  async add(event) {
    event.preventDefault();
    this.syncBaseVariant();
    const sel = this.selected();
    if (!sel.length || this.busy) return;
    this.busy = true;
    if (this.addButton) this.addButton.setAttribute('aria-busy', 'true');

    // Ask the server to re-render the same sections the cart line-item
    // components own, so they can morph themselves from the response.
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const formData = new FormData();
    sel.forEach((cb, index) => {
      formData.append('items[' + index + '][id]', cb.dataset.variantId);
      formData.append('items[' + index + '][quantity]', '1');
    });
    if (sectionIds.length) formData.append('sections', Array.from(new Set(sectionIds)).join(','));

    const lines = sel.map((cb) => ({ merchandiseId: cb.dataset.variantId, quantity: 1 }));
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines,
        promise: deferred.promise,
      })
    );

    try {
      // Clean FormData POST — let the browser set the multipart Content-Type and
      // ask for JSON back (the previous config asked for text/html, so r.json()
      // choked on the HTML response and the drawer never opened).
      const response = await fetch(CART_ADD_URL, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      }).then((r) => r.json());

      if (response.status) {
        deferred.reject(new Error(response.description || response.message || 'Add to cart failed'));
        return;
      }

      // Refresh the cart line-item components — guarded so an events-API hiccup
      // can never stop the drawer from opening below.
      try {
        const ajaxCart = await fetch(CART_URL + '.js', {
          headers: { Accept: 'application/json' },
        }).then((r) => r.json());

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            items: ajaxCart.items,
            source: 'juice-complete-setup',
            sections: response.sections,
            didError: false,
          },
        });
      } catch (refreshError) {
        console.warn('[juice-complete-setup] cart refresh event failed', refreshError);
      }

      // Always open the cart drawer on a successful add.
      const drawer = document.querySelector('#cart-drawer');
      if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();

      // Reset only the accessory rows; keep the base ("this item") ticked.
      this.checkboxes.forEach((cb) => {
        if (cb !== this.baseCheckbox) cb.checked = false;
      });
      this.update();
    } catch (error) {
      console.error('[juice-complete-setup] add failed', error);
      deferred.reject(error);
    } finally {
      this.busy = false;
      if (this.addButton) this.addButton.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('juice-complete-setup')) {
  customElements.define('juice-complete-setup', JuiceCompleteSetup);
}
